import { useState } from "react";
import { Lock, ShieldAlert, Mail, Loader2 } from "lucide-react";
import { useAuth } from "./AuthContext";
import { BHLMark } from "../BHLLogo";

export const LoginScreen = () => {
  const { login, loggingIn, loginError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleDenied, setRoleDenied] = useState(false);
  const [localError, setLocalError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setRoleDenied(false);
    const result = await login({ email, password });
    if (result.ok) {
      const u = result.user || {};
      const isSuper =
        u.role === "platform_superadmin" ||
        u.platform_role === "platform_superadmin" ||
        (Array.isArray(u.roles) && u.roles.includes("platform_superadmin"));
      if (!isSuper) {
        setRoleDenied(true);
      }
    }
  };

  return (
    <div
      data-testid="login-screen"
      className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#0d3b66] mb-3 shadow-sm">
            <BHLMark tone="light" size={32} />
          </span>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            Blue Harbor Labs
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">
            Control Center
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Sign in with your ShelfLife platform account.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-4"
        >
          {roleDenied && (
            <div
              data-testid="login-role-denied"
              className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Not authorized</div>
                <div className="text-xs">
                  You signed in successfully, but your account does not have the
                  <span className="font-mono mx-1">platform_superadmin</span>
                  role required to access the Control Center.
                </div>
              </div>
            </div>
          )}

          {!roleDenied && (loginError || localError) && (
            <div
              data-testid="login-error"
              className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              {localError || loginError}
            </div>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Email
            </span>
            <div className="mt-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0d3b66]/30 focus:border-[#0d3b66]"
                placeholder="rafael@blueharborlabs.com"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-slate-500">
              Password
            </span>
            <div className="mt-1 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0d3b66]/30 focus:border-[#0d3b66]"
                placeholder="••••••••"
              />
            </div>
          </label>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loggingIn || !email || !password}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0d3b66] text-white px-3 py-2 text-sm font-semibold hover:bg-[#0a2e52] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>

          <div className="text-[11px] text-slate-500 text-center pt-2 border-t border-slate-100">
            Phase 2C live mode · Requires{" "}
            <span className="font-mono">platform_superadmin</span>
          </div>
        </form>
      </div>
    </div>
  );
};
